import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { createFileRoute } from "@tanstack/react-router";
import { SquareTerminal } from "lucide-react";
import { useState } from "react";

/**
 * `/connect/hub?code=…`: a terminal running `lair setup` asks to act
 * as you. The code here must be the one it shows; allowing mints a token with
 * only the scopes listed, which the terminal collects once.
 */

type ConnectRequest = {
  code: string;
  host: string;
  scopes: Array<{ key: string; label: string }>;
};

export const Route = createFileRoute("/_app/connect/hub")({
  validateSearch: (search: Record<string, unknown>): { code?: string } =>
    typeof search.code === "string" ? { code: search.code } : {},
  loaderDeps: ({ search }) => ({ code: search.code }),
  loader: ({ deps }) =>
    deps.code
      ? api
          .get<ConnectRequest>(`/api/agent-connect/request/${encodeURIComponent(deps.code)}`)
          .catch(() => null)
      : null,
  component: ConnectHubAgentPage,
});

function ConnectHubAgentPage() {
  const request = Route.useLoaderData();
  const [answer, setAnswer] = useState<"allowed" | "denied" | null>(null);
  const respond = useApiMutation(
    (allow: boolean) => api.post(`/api/agent-connect/request/${request?.code ?? ""}`, { allow }),
    { fallbackMessage: "Couldn't answer the request." },
  );

  const decide = async (allow: boolean) => {
    await respond.mutate(allow);
    setAnswer(allow ? "allowed" : "denied");
  };

  return (
    <div className="mx-auto max-w-lg py-10">
      <Card data-testid="connect-hub">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SquareTerminal className="h-5 w-5" />
            Connect lair
          </CardTitle>
          <CardDescription>
            A terminal asks to act as you: Hub's agents on your own Claude subscription.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!request ? (
            <p className="text-sm text-muted-foreground">
              This request has expired or was already answered. Run{" "}
              <code className="rounded bg-muted px-1">lair setup</code> again.
            </p>
          ) : answer === "allowed" ? (
            <p className="text-sm">Done. Go back to your terminal, it carries on by itself.</p>
          ) : answer === "denied" ? (
            <p className="text-sm">Refused. The terminal gets nothing.</p>
          ) : (
            <>
              <div className="rounded-lg border border-border p-3 text-center">
                <p className="text-xs text-muted-foreground">Code shown in the terminal</p>
                <p className="font-mono text-2xl tracking-widest" data-testid="connect-code">
                  {request.code}
                </p>
                <p className="text-xs text-muted-foreground mt-1">on {request.host}</p>
              </div>
              <div className="text-sm">
                <p className="font-medium">It will be able to:</p>
                <ul className="list-disc pl-5 text-muted-foreground">
                  {request.scopes.map((s) => (
                    <li key={s.key}>{s.label}</li>
                  ))}
                </ul>
              </div>
              <p className="text-xs text-muted-foreground">
                Allow it only if you started it and the code matches. You can revoke it any time in
                Profile → API tokens.
              </p>
              <div className="flex gap-2">
                <Button onClick={() => void decide(true)} disabled={respond.pending}>
                  Allow
                </Button>
                <Button
                  variant="outline"
                  onClick={() => void decide(false)}
                  disabled={respond.pending}
                >
                  Deny
                </Button>
              </div>
              {respond.error && <p className="text-sm text-destructive">{respond.error}</p>}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
